import React, { useState } from 'react';

// ─── ConfirmModal ───
export function ConfirmModal({ title, message, confirmLabel = 'Confirm', destructive = false, onClose, onConfirm }: {
  title: string;
  message: string;
  confirmLabel?: string;
  destructive?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-900">{title}</h3>
        </div>
        <div className="p-6 text-sm text-slate-700 whitespace-pre-line">{message}</div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 py-2.5 text-white rounded-lg font-semibold text-sm ${destructive ? 'bg-red-600 hover:bg-red-700' : 'bg-[#0f172a] hover:bg-slate-800'}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TextPromptModal ───
export function TextPromptModal({ title, label, placeholder, required = true, onClose, onSubmit }: {
  title: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  onClose: () => void;
  onSubmit: (value: string) => void;
}) {
  const [text, setText] = useState('');
  const [err, setErr] = useState('');

  const submit = () => {
    if (required && !text.trim()) {
      setErr('This field is required');
      return;
    }
    onSubmit(text.trim());
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-900">{title}</h3>
        </div>
        <div className="p-6 space-y-2">
          <label className="block text-sm font-semibold text-slate-700">{label}</label>
          <textarea
            value={text}
            onChange={(e) => { setText(e.target.value); setErr(''); }}
            placeholder={placeholder}
            rows={3}
            autoFocus
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
          />
          {err && <p className="text-sm text-red-600">{err}</p>}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">
            Cancel
          </button>
          <button onClick={submit} className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm">
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FormModal — generic multi-field form modal ───
export type FormField = {
  name: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'checkbox' | 'textarea';
  required?: boolean;
  placeholder?: string;
  options?: { label: string; value: string }[];
  defaultValue?: any;
  onChange?: (value: any, currentValues: any, setValue: (name: string, val: any) => void) => void;
  min?: number;
  step?: number;
  helpText?: string;
};

export function FormModal({ title, description, fields, initial, submitLabel = 'Save', onClose, onSubmit }: {
  title: string;
  description?: string;
  fields: FormField[];
  initial?: Record<string, any>;
  submitLabel?: string;
  onClose: () => void;
  onSubmit: (values: Record<string, any>) => Promise<void> | void;
}) {
  const init: Record<string, any> = {};
  fields.forEach((f) => {
    init[f.name] = f.defaultValue ?? (f.type === 'checkbox' ? false : '');
  });
  const [values, setValues] = useState<Record<string, any>>(init);
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const setField = (name: string, v: any) => {
    setValues((prev) => ({ ...prev, [name]: v }));
    setErr('');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    for (const f of fields) {
      if (f.required && (values[f.name] === '' || values[f.name] == null)) {
        setErr(`${f.label} is required`);
        return;
      }
    }
    setSubmitting(true);
    setErr('');
    try {
      await onSubmit(values);
      // Parent closes the modal on success; we stay open until then.
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? e?.message ?? 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl my-auto">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-900">{title}</h3>
          {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {fields.map((f) => (
            <div key={f.name}>
              {f.type !== 'checkbox' && (
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  {f.label}
                  {f.required && ' *'}
                </label>
              )}
              {f.type === 'text' && (
                <input type="text" value={values[f.name] ?? ''}
                  onChange={(e) => setField(f.name, e.target.value)}
                  placeholder={f.placeholder} required={f.required}
                  className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" />
              )}
              {f.type === 'textarea' && (
                <textarea rows={3} value={values[f.name] ?? ''}
                  onChange={(e) => setField(f.name, e.target.value)}
                  placeholder={f.placeholder} required={f.required}
                  className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" />
              )}
              {f.type === 'number' && (
                <input type="number" value={values[f.name] ?? ''}
                  onChange={(e) => setField(f.name, e.target.value)}
                  placeholder={f.placeholder} required={f.required}
                  min={f.min} step={f.step}
                  className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" />
              )}
              {f.type === 'select' && (
                <select value={values[f.name] ?? ''}
                  onChange={(e) => setField(f.name, e.target.value)}
                  required={f.required}
                  className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white">
                  <option value="">{f.placeholder ?? 'Choose…'}</option>
                  {(f.options ?? []).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              )}
              {f.type === 'checkbox' && (
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={!!values[f.name]}
                    onChange={(e) => setField(f.name, e.target.checked)} />
                  {f.label}
                </label>
              )}
              {f.helpText && <p className="text-xs text-slate-500 mt-1">{f.helpText}</p>}
            </div>
          ))}
          {err && <p className="text-sm text-red-600">{err}</p>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={submitting}
              className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
              {submitting ? 'Saving…' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


export function PinModal({ title, subtitle, onClose, onConfirm }: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  onConfirm: (pin: string) => void;
}) {
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const submit = () => {
    if (!/^\d{4}$/.test(pin)) { setErr('Enter 4 digits'); return; }
    onConfirm(pin);
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-900">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        <div className="p-6 space-y-2">
          <input
            type="password"
            inputMode="numeric"
            maxLength={4}
            autoFocus
            value={pin}
            onChange={(e) => { setPin(e.target.value.replace(/\D/g, '')); setErr(''); }}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            className="w-full text-center text-3xl tracking-[0.5em] font-mono border-2 border-slate-300 rounded-xl py-3"
          />
          {err && <p className="text-sm text-red-600 text-center">{err}</p>}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">
            Cancel
          </button>
          <button onClick={submit} disabled={pin.length !== 4}
            className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
